/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useServiceContext } from '../../root'
import FsiPickerNode, { TreeNode } from '../FsiPickerNode'
import Loader from '../shared/Loader'
import { FsiType } from '../../constants/fsi'
import AddNewFolderModalWrapper from '../fsi-picker/AddNewFolderModalWrapper'
import { extend } from 'lodash'

// Constants
const SELECTOR = {
  all: 'all',
  file: 'file',
  folder: 'folder'
} as const

type SelectorType = (typeof SELECTOR)[keyof typeof SELECTOR]

// TypeScript interfaces
export interface FsiPickerOptions {
  selectedId?: number | null
  openIds?: number[]
  leafIds?: number[] | null
  rootId?: number | null
  showRC?: boolean
  maxDepth?: number | null
  showRadio?: boolean
  skipLevel?: number | null
  showSystem?: boolean
  extendParams?: Record<string, any>
  onlyFoldersAndDocuments?: boolean | null
  onlyFoldersAndPlaceholders?: boolean | null
  onlyFoldersAndDocumentsWithWriteAccess?: boolean | null
  selector?: SelectorType
  eosMediaTransferMode?: boolean
  hidePickerLoader?: boolean
  showCheckBox?: boolean
}

export interface FsiPickerProps {
  options?: Partial<FsiPickerOptions>
  onSelect?: (selectedIds: (number | string)[] | TreeNode, baseNodes?: TreeNode[] | undefined) => void
  onSetStudyFolderId?: (id: number) => void
  showAddNewFolderModal?: boolean
  isNodeSelectable?: (node: TreeNode) => boolean
  isNodeExpandable?: (node: TreeNode) => boolean
  stateSelection?: string
  isSetupUsersAndWorkflowsFlow?: boolean
  isUserSponsorInstructionCoordinator?: boolean
}

interface AddNewFolderModalClasses {
  modalClass: string
  backDropClass: string
}

const FsiPicker: React.FC<FsiPickerProps> = (props) => {
  const context = useServiceContext()
  const { constant, service, api, $rootScope } = context
  const { translate } = service.filter

  // Merge default options with provided options
  const options: FsiPickerOptions = {
    selectedId: null,
    openIds: [],
    leafIds: [],
    rootId: null,
    showRC: true,
    maxDepth: null,
    showRadio: undefined,
    skipLevel: null,
    showSystem: undefined,
    extendParams: {},
    onlyFoldersAndDocuments: null,
    onlyFoldersAndPlaceholders: null,
    onlyFoldersAndDocumentsWithWriteAccess: null,
    selector: SELECTOR.all,
    ...props.options
  }

  // State
  const [isLoading, setIsLoading] = useState(false)
  const [baseNodes, setBaseNodes] = useState<TreeNode[]>([])
  const [selected, setSelected] = useState<TreeNode | null>(null)
  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([])
  const [partialSelectedIds, setPartialSelectedIds] = useState<(number | string)[]>([])
  const [addNewFolderModalClasses, setAddNewFolderModalClasses] = useState<AddNewFolderModalClasses | null>(null)
  const [selectedStudyFolderId, setSelectedStudyFolderId] = useState<number>(0)
  const [isExpandActionInProgress, setIsExpandActionInProgress] = useState(false)

  // Refs for mutable arrays that don't need to trigger re-renders
  const baseNodesRef = useRef<TreeNode[]>([])
  const expandedNodesRef = useRef<TreeNode[]>([])
  const expandedLeafNodesRef = useRef<TreeNode[]>([])
  const loadingActionsRef = useRef(0)
  const userCancelledLoadingRef = useRef(false)
  const defReqAbortRef = useRef<any>(null)

  // Initialize on mount
  useEffect(() => {
    const unsubscribe = $rootScope.$on('updateFiles', (event: any, data: string) => {
      if (['ADD_FOLDER'].indexOf(data) >= 0) {
        loadData()
      }
    })

    // Also listen for window CustomEvent (used by AddFolderModal)
    const handleWindowUpdateFiles = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (['ADD_FOLDER'].indexOf(detail) >= 0) {
        loadData()
      }
    }
    window.addEventListener('updateFiles', handleWindowUpdateFiles)

    loadData()

    return () => {
      unsubscribe()
      window.removeEventListener('updateFiles', handleWindowUpdateFiles)
    }
  }, [])

  // Initialize selectedIds from options.selectedId
  useEffect(() => {
    if (options.selectedId && !selectedIds.includes(options.selectedId)) {
      setSelectedIds([options.selectedId])
    }
  }, [options.selectedId])

  // Handle selectedIds changes for checkbox mode
  useEffect(() => {
    if (!selectedIds.length && options.showCheckBox) {
      if (typeof props.onSetStudyFolderId === 'function') {
        props.onSetStudyFolderId(selectedStudyFolderId)
      }
      if (partialSelectedIds.length) {
        setPartialSelectedIds([])
      }
    }
  }, [selectedIds, options.showCheckBox, partialSelectedIds.length])

  // Helper: Start loading
  const startLoading = useCallback(() => {
    loadingActionsRef.current++
    setIsLoading(true)
  }, [])

  // Helper: Finish loading
  const finishLoading = useCallback(() => {
    loadingActionsRef.current = loadingActionsRef.current > 0 ? loadingActionsRef.current - 1 : 0
    if (loadingActionsRef.current === 0) {
      setIsLoading(false)
    }
  }, [])

  // Helper: Cancel loading
  const cancelLoading = useCallback(() => {
    if (!userCancelledLoadingRef.current) {
      if (expandedNodesRef.current) {
        // Remove leaves that have not yet formed a branch
        const missingNodes = expandedNodesRef.current.filter((node) =>
          expandedLeafNodesRef.current.find((leaf) => leaf.id === node.id)
        )
        removeMultipleFromLeaves(missingNodes)
      }

      userCancelledLoadingRef.current = true
      loadingActionsRef.current = 0
      setIsLoading(false)

      if (defReqAbortRef.current) {
        defReqAbortRef.current.resolve()
      }
    }
  }, [])

  // Helper: Update nodes state
  const updateNodes = useCallback((callback?: () => void) => {
    setBaseNodes([...baseNodesRef.current])
    if (callback) callback()
  }, [])

  // Helper: Transform FSI to TreeNode
  const transformFsiToNode = useCallback((fsi: any): Partial<TreeNode> => {
    return {
      id: fsi.id,
      name: fsi.name,
      type: fsi.type,
      parentId: fsi.parentId,
      templateId: fsi.templateId,
      ...(fsi.systemFolderType && { systemFolderType: fsi.systemFolderType })
    }
  }, [])

  // Helper: Get file
  const getFile = useCallback(
    (id: number) => {
      return api.files.getFile(id)
    },
    [api]
  )

  // Helper: Find node by ID recursively
  const findNodeById = useCallback((node: TreeNode, id: number | string): TreeNode | null => {
    if (node.id === id) return node
    if (node.children) {
      for (const child of node.children) {
        const found = findNodeById(child, id)
        if (found) return found
      }
    }
    return null
  }, [])

  // Helper: Get all descendant IDs
  const getAllDescendantIds = useCallback((node: TreeNode, isPreSelected: boolean): (number | string)[] => {
    let ids: (number | string)[] = [node.id]
    if (node.children) {
      node.children.forEach((child) => {
        ids = ids.concat(getAllDescendantIds(child, isPreSelected))
      })
    }
    return ids
  }, [])

  // Helper: Check if study is selected
  const checkIfStudyIsSelected = useCallback(
    (node: TreeNode, isPreSelected: boolean) => {
      if (node.eosFilePicker?.studyId) {
        const activeStudyId = isPreSelected
          ? node.eosFilePicker.study || selectedIds.length === 1
            ? 0
            : node.eosFilePicker.studyId
          : node.eosFilePicker.studyId

        setSelectedStudyFolderId(activeStudyId)
        if (typeof props.onSetStudyFolderId === 'function') {
          props.onSetStudyFolderId(activeStudyId)
        }
      }
    },
    [selectedIds.length, props]
  )

  // Helper: Populate children selection
  const populateChildrenSelection = useCallback(
    (selectedIdsData: (number | string)[], nodeId: number | string, node: TreeNode): (number | string)[] => {
      const targetNode = findNodeById(node, nodeId)
      if (targetNode) {
        const isPreSelected = selectedIdsData.includes(nodeId)
        checkIfStudyIsSelected(targetNode, isPreSelected)
        const descendantIds = getAllDescendantIds(targetNode, isPreSelected)

        if (isPreSelected) {
          selectedIdsData = selectedIdsData.filter((id) => !descendantIds.includes(id))
        } else {
          selectedIdsData = [...selectedIdsData, ...descendantIds]
        }
        return Array.from(new Set(selectedIdsData))
      }
      return selectedIdsData
    },
    [findNodeById, checkIfStudyIsSelected, getAllDescendantIds]
  )

  // Helper: Propagate selection state upward to ancestors.
  // When all children of a parent are selected, the parent is auto-selected.
  // When not all children are selected, the parent is removed from selection.
  const propagateSelectionToAncestors = useCallback(
    (selectedIdsData: (number | string)[], startNode: TreeNode): (number | string)[] => {
      const result = [...selectedIdsData]
      let current = startNode.parent
      while (current) {
        // Never auto-select the root node
        if (options.rootId != null && current.id === options.rootId) break

        if (current.children && current.children.length > 0) {
          const allChildrenSelected = current.children.every((child) => result.includes(child.id))
          if (allChildrenSelected) {
            if (!result.includes(current.id)) {
              result.push(current.id)
            }
          } else {
            const idx = result.indexOf(current.id)
            if (idx !== -1) {
              result.splice(idx, 1)
            }
          }
        }
        current = current.parent
      }
      return result
    },
    [options.rootId]
  )

  // Action: Select node
  const selectAction = useCallback(
    (node: TreeNode) => {
      cancelLoading()
      let selectedIdsData = [...selectedIds]

      if (options.showCheckBox) {
        selectedIdsData = populateChildrenSelection(selectedIdsData, node.id, node)
        selectedIdsData = propagateSelectionToAncestors(selectedIdsData, node)
      }

      setSelected(node)
      setSelectedIds(options.showCheckBox ? selectedIdsData : [node.id])

      if (typeof props.onSelect === 'function') {
        if (options.showCheckBox) {
          props.onSelect(Array.from(new Set(selectedIdsData)), baseNodesRef.current)
          return
        }
        props.onSelect(node, baseNodesRef.current)
      }
    },
    [selectedIds, options.showCheckBox, cancelLoading, populateChildrenSelection, propagateSelectionToAncestors, props]
  )

  // Helper: Remove from leaves
  const removeFromLeaves = useCallback((node: TreeNode) => {
    if (node) {
      const expandedLeafIndex = expandedLeafNodesRef.current.findIndex((leaf) => leaf.id === node.id)
      if (expandedLeafIndex !== -1) {
        expandedLeafNodesRef.current.splice(expandedLeafIndex, 1)
      }

      const expandedIndex = expandedNodesRef.current.findIndex((exp) => exp.id === node.id)
      if (expandedIndex !== -1) {
        expandedNodesRef.current.splice(expandedIndex, 1)
      }
    }
  }, [])

  // Helper: Remove multiple from leaves
  const removeMultipleFromLeaves = useCallback(
    (nodes: TreeNode[]) => {
      nodes.forEach((node) => removeFromLeaves(node))
    },
    [removeFromLeaves]
  )

  // Helper: Add to expanded
  const addToExpanded = useCallback((node: TreeNode) => {
    node.isExpanded = true
    if (!expandedNodesRef.current.find((n) => n.id === node.id)) {
      expandedNodesRef.current.push(node)
    }
  }, [])

  // Helper: Add to expanded recursively
  const addToExpandedRecursive = useCallback(
    (node: TreeNode) => {
      addToExpanded(node)
      if (node.parent) {
        addToExpandedRecursive(node.parent)
      }
    },
    [addToExpanded]
  )

  // Helper: Remove from expanded recursively
  const removeFromExpandedRecursive = useCallback((node: TreeNode, stopNode: TreeNode) => {
    const expandedNode = expandedNodesRef.current.find((n) => n.id === node.id)
    if (expandedNode) {
      const index = expandedNodesRef.current.indexOf(expandedNode)
      expandedNodesRef.current.splice(index, 1)

      if (expandedNode.parent && expandedNode.parent !== stopNode) {
        removeFromExpandedRecursive(expandedNode.parent, stopNode)
      }
    }
  }, [])

  // Helper: Add to expanded leaves
  const addToExpandedLeaves = useCallback((folder: TreeNode) => {
    if (!expandedLeafNodesRef.current.find((n) => n.id === folder.id)) {
      expandedLeafNodesRef.current.push(folder)
    }

    if (!expandedNodesRef.current.find((n) => n.id === folder.id)) {
      expandedNodesRef.current.push(folder)
    }
  }, [])

  // Helper: Is descendant
  const isDescendant = useCallback((id: number | string, folder: TreeNode): boolean => {
    if (folder.parent) {
      if (folder.parent.id === id) {
        return true
      } else {
        return isDescendant(id, folder.parent)
      }
    }
    return false
  }, [])

  // Helper: Set levels according to ancestor
  const setLevelsAccordingToAncestor = useCallback((node: TreeNode, nodes: TreeNode[] = []) => {
    if (node.level || !node.parent) {
      const startLevel = typeof node.level === 'number' ? node.level : 1
      nodes.reverse().reduce((level, n) => {
        n.level = level + 1
        return n.level
      }, startLevel)
    } else {
      nodes.push(node)
      setLevelsAccordingToAncestor(node.parent, nodes)
    }
  }, [])

  // Helper: Find ancestor with level
  const findAncestorWithLevel = useCallback((node: TreeNode): TreeNode | null => {
    if (node.level) {
      return node
    } else if (node.parent) {
      return findAncestorWithLevel(node.parent)
    }
    return null
  }, [])

  // Helper: Set levels
  const setLevels = useCallback((nodes: TreeNode[], level: number) => {
    nodes.forEach((node) => {
      if (typeof node.level !== 'number') {
        node.level = level
        if (node.children) {
          setLevels(node.children, level + 1)
        }
      }
    })
  }, [])

  // Helper: Create RC node
  // Note: Not using useCallback here because we need to read fresh browser data
  // that may be loaded asynchronously by refreshUserPolicies()
  const createRcNode = (): TreeNode | null => {
    const rcFolderResult = service.auth.getBrowser(constant.file.systemFolderType.REGULATED_CONTENT)
    return {
      ...rcFolderResult,
      name: translate('BROWSER_VIEW_REGULATED_CONTENT'),
      level: 1,
      type: constant.file.type.FOLDER,
      isExpanded: true,
      children: [],
      wasLoaded: true // Mark as already loaded to prevent API overwrite
    } as TreeNode
  }

  // Helper: Create system root nodes
  const createSystemRootNodes = useCallback((): TreeNode => {
    const systemFolders: TreeNode[] = []
    const sysDocsRoot: TreeNode = {
      name: translate('BROWSER_VIEW_SYSTEM_DOCUMENTS'),
      level: 1,
      virtualFolder: true,
      hasChildren: true,
      id: 'BROWSER_VIEW_SYSTEM_DOCUMENTS',
      type: constant.file.type.FOLDER,
      isExpanded: false,
      children: systemFolders,
      systemFolderType: constant.file.systemFolderType.SYSTEM_DOCUMENTS
    }

    if (service.auth.getRole() !== constant.user.roles.ETMF_SPONSOR) {
      systemFolders.push(
        createVirtualFolderNode(
          constant.file.systemFolderType.DOCUMENT_MIGRATIONS_AND_EXPORTS,
          'DOCUMENTS_MIGRATION_EXPORTS'
        )
      )
      systemFolders.push(
        createVirtualFolderNode(constant.file.systemFolderType.EMAIL_UPLOADS, 'SYSTEM_DOCUMENTS_EMAIL_UPLOADS')
      )

      if (service.access.isAllowed('documentsAccessForms')) {
        pushSystemFolderNode(systemFolders, constant.file.systemFolderType.FORMS)
      }

      systemFolders.push(
        createVirtualFolderNode(
          constant.file.systemFolderType.SETTINGS_AND_SYSTEM_EXPORTS_IMPORTS,
          'SETTINGS_AND_SYSTEM_ITEMS_EXPORTS_IMPORTS'
        )
      )

      pushSystemFolderNode(systemFolders, constant.file.systemFolderType.INIT_ETMF_FILES)

      if (service.access.isAllowed('documentsAccessTemplates')) {
        pushSystemFolderNode(systemFolders, constant.file.systemFolderType.TEMPLATES)
      }
    }

    systemFolders.push(createVirtualFolderNode(constant.file.systemFolderType.REPORTS, 'REPORTS_EXPORT_TITLE'))

    systemFolders.forEach((folder) => {
      Object.assign(folder, {
        parent: sysDocsRoot,
        type: constant.file.type.FOLDER,
        parentId: 'BROWSER_VIEW_SYSTEM_DOCUMENTS'
      })
    })

    return sysDocsRoot
  }, [service, constant, translate])

  // Helper: Create site root node
  const createSiteRootNode = useCallback((): TreeNode => {
    const userRole = service.auth.getRole()

    return {
      name: translate('DASHBOARD_MY_SITES'),
      level: 1,
      id: -1,
      hasChildren: true,
      virtualFolder: false,
      type: constant.file.type.FOLDER,
      children: [],
      isExpanded: userRole === constant.user.roles.ETMF_SITE_USER,
      isSiteFolder: true
    }
  }, [service, constant, translate])

  // Helper: Create study root node
  const createStudyRootNode = useCallback((): TreeNode => {
    const userRole = service.auth.getRole()

    return {
      name: translate('DASHBOARD_MY_STUDIES'),
      level: 1,
      id: -1,
      hasChildren: true,
      virtualFolder: false,
      type: constant.file.type.FOLDER,
      children: [],
      isExpanded: userRole === constant.user.roles.ETMF_STUDY_USER,
      isStudyFolder: true
    }
  }, [service, constant, translate])

  // Helper: Create system folder node
  const createSystemFolderNode = useCallback(
    (folderType: string): TreeNode => {
      const folder = service.auth.getBrowser(folderType)
      return {
        level: 2,
        id: (folder.id || folder.name) as number | string,
        name: String(folder.name),
        hasChildren: true,
        type: constant.file.type.FOLDER,
        isExpanded: false,
        children: [],
        systemFolderType: folderType
      }
    },
    [service, constant]
  )

  // Helper: Create virtual folder node
  const createVirtualFolderNode = useCallback(
    (folderType: string, nameKey: string): TreeNode => {
      const name = translate(nameKey)
      return {
        level: 2,
        id: name,
        isExpanded: false,
        hasChildren: false,
        name,
        type: constant.file.type.FOLDER,
        systemFolderType: folderType
      }
    },
    [translate, constant]
  )

  // Helper: Push system folder node
  const pushSystemFolderNode = useCallback(
    (nodes: TreeNode[], folderType: string) => {
      const folder = service.auth.getBrowser(folderType)
      if (folder && folder.id) {
        nodes.push(createSystemFolderNode(folderType))
      }
    },
    [service, createSystemFolderNode]
  )

  // Helper: Get service options
  const getServiceOptions = useCallback(() => {
    return {
      filter: {
        obsolete: false,
        sortBy: 'name',
        sortDesc: false
      },
      options: {
        onlyFoldersAndDocuments: options.onlyFoldersAndDocuments ?? undefined,
        onlyFoldersAndPlaceholders: options.onlyFoldersAndPlaceholders ?? undefined,
        onlyFoldersAndDocumentsWithWriteAccess: options.onlyFoldersAndDocumentsWithWriteAccess ?? undefined,
        onlyFolders: options.selector === SELECTOR.folder,
        ...options.extendParams
      }
    }
  }, [options])

  // Helper: Process children
  const processChildren = useCallback(
    (folder: TreeNode, childNodes: any[]): TreeNode => {
      if (!folder.fileName && folder.name) {
        folder.fileName = folder.name
      }

      folder.hasChildren = childNodes.length > 0

      // Legacy: use isNodeVisible if provided for visibility filtering.
      // NOTE: isNodeSelectable must NOT be used here — it only controls whether
      // a node's radio/checkbox is enabled, not whether the node is visible.
      // Using it as a visibility filter would hide non-selectable parent folders
      // that contain selectable children (e.g., non-study folders with nested studies in EOS).
      if (typeof (props as any).isNodeVisible === 'function') {
        folder.children = childNodes.filter((i) => (props as any).isNodeVisible(i))
      } else {
        folder.children = childNodes
      }

      if (folder.hasChildren) {
        if (options.selector === SELECTOR.all || options.selector === SELECTOR.file) {
          folder.children = folder.children?.map((node) => {
            if (node.type === constant.file.type.FOLDER) {
              return { ...node, hasChildren: true }
            }
            return node
          })
        } else {
          // folders only
          folder.children?.forEach((node) => {
            node.hasChildren = node.children ? !!node.children.length : true
            node.type = constant.file.type.FOLDER
          })
        }
      }

      folder.children?.forEach((node) => {
        if (folder.level) {
          node.level = folder.level + 1
        }

        if (node.fileName) {
          node.name = node.fileName
        }

        Object.assign(node, {
          parent: folder,
          parentId: folder.id,
          systemFolderType: folder.systemFolderType
        })
      })

      // Ensure UI updates after children assignment
      setBaseNodes([...baseNodesRef.current])

      return folder
    },
    [options, constant, props]
  )

  // Helper: Node replacement
  const nodeReplacement = useCallback(
    (node: TreeNode, list: TreeNode[]): boolean => {
      const replacedChild = list.find((n) => n.id === node.id)
      if (replacedChild && replacedChild !== node) {
        const indexForReplacement = list.indexOf(replacedChild)
        if (indexForReplacement >= 0) {
          delete replacedChild.children
          list[indexForReplacement] = node
          Object.assign(node, replacedChild)

          removeFromLeaves(replacedChild)
          return true
        }
      }
      return false
    },
    [removeFromLeaves]
  )

  // Helper: Get FSIs filepicker
  const getFsisFilepicker = useCallback(
    (folder: TreeNode, filter: any, fsiFilterOptions: any): Promise<boolean> => {
      return new Promise((resolve) => {
        if (typeof folder.id === 'string') {
          // Virtual folder with string ID - skip API call
          folder.wasLoaded = true
          finishLoading()
          resolve(true)
          return
        }

        api.files
          .getFsisFilepicker(folder.id, filter, fsiFilterOptions)
          .then((data: any) => {
            const children = data
            folder.wasLoaded = true
            processChildren(folder, children)
            finishLoading()
            resolve(true)
          })
          .catch(() => {
            finishLoading()
            resolve(true)
          })
      })
    },
    [api, processChildren, finishLoading]
  )

  // Helper: Get ETMF
  const getEtmf = useCallback(
    (folder: TreeNode, etmfService: Function): Promise<TreeNode> => {
      return new Promise((resolve) => {
        // Legacy: fetch pageSize=1, then fetch all with totalElements
        etmfService({ pageSize: 1 }, true)
          .then((data: any) => {
            if (data && typeof data.totalElements === 'number') {
              etmfService({ pageSize: data.totalElements }, true)
                .then((allData: any) => {
                  const fsis = (allData.elements || []).map((site: any) => {
                    // Legacy logic: if site.siteName or site.studyName, extend site with name
                    if (site.siteName || site.studyName) {
                      extend(site, {
                        name: site.siteName || site.studyName
                      })
                    }
                    const st = {
                      type: constant.file.type.FOLDER,
                      id: site.fsiId,
                      name: site.siteName
                        ? site.siteName
                        : site.envName
                          ? site.studyName + ' (' + site.envName + ')'
                          : site.studyName
                            ? site.studyName
                            : site.name
                    }
                    if (site.siteName || site.studyName) {
                      extend(st, {
                        name: site.siteName || site.studyName
                      })
                    }
                    return st
                  })
                  folder.wasLoaded = true
                  processChildren(folder, fsis)
                  finishLoading()
                  resolve(folder)
                })
                .catch(() => {
                  finishLoading()
                  resolve(folder)
                })
            } else {
              // No data found
              folder.wasLoaded = true
              processChildren(folder, [])
              finishLoading()
              resolve(folder)
            }
          })
          .catch(() => {
            finishLoading()
            resolve(folder)
          })
      })
    },
    [constant, processChildren, finishLoading]
  )

  // Helper: Load child nodes
  const loadChildNodes = useCallback(
    (folder: TreeNode): Promise<any> | undefined => {
      const userRole = service.auth.getRole()

      if (!folder.virtualFolder && typeof folder.id === 'number') {
        if (!options.hidePickerLoader) {
          startLoading()
        }

        if (folder.isStudyFolder) {
          const getStudiesCall =
            userRole === constant.user.roles.ETMF_STUDY_USER ||
            userRole === constant.user.roles.ETMF_SPECIALIST ||
            userRole === constant.user.roles.ETMF_SPONSOR
              ? api.etmf.getMyEnvironments
              : api.etmf.getMyStudies

          return getEtmf(folder, getStudiesCall)
        } else if (folder.isSiteFolder) {
          return getEtmf(folder, api.etmf.getMySites)
        } else {
          const serviceOptions = getServiceOptions()
          return getFsisFilepicker(folder, serviceOptions.filter, serviceOptions.options)
        }
      }
    },
    [service, constant, options, api, startLoading, getEtmf, getFsisFilepicker, getServiceOptions]
  )

  // Helper: Load base node
  const loadBaseNode = useCallback(
    (node: TreeNode): Promise<void> => {
      return new Promise((resolve) => {
        const serviceOptions = getServiceOptions()

        // Skip loading if node has wasLoaded flag (e.g., RC node from createRcNode)
        if (node.wasLoaded) {
          if (node.isExpanded) {
            loadChildNodes(node)
          }
          resolve()
          return
        }

        if (typeof node.id === 'number' && node.id > 0) {
          api.files
            .getFilepickerFsi(node.id, serviceOptions.filter, serviceOptions.options)
            .then((data: any) => {
              if (data) {
                // Preserve the custom name if it was already set (e.g., translated name)
                const customName = node.name
                Object.assign(node, transformFsiToNode(data))
                if (customName && customName !== data.name) {
                  node.name = customName
                }
              }

              if (node.isExpanded) {
                loadChildNodes(node)
              }
              resolve()
            })
            .catch(resolve)
        } else {
          resolve()
        }
      })
    },
    [api, getServiceOptions, transformFsiToNode, loadChildNodes]
  )

  // Helper: Expand node
  const expandNode = useCallback(
    (node: TreeNode): Promise<boolean> => {
      return new Promise((resolve) => {
        addToExpanded(node)
        const promise = node.wasLoaded ? null : loadChildNodes(node)

        if (promise) {
          promise.then(() => resolve(true))
        } else {
          resolve(false)
        }
      })
    },
    [addToExpanded, loadChildNodes]
  )

  // Action: Expand
  const expandAction = useCallback(
    (node: TreeNode) => {
      cancelLoading()
      if (node.parent) {
        removeFromLeaves(node.parent)
      }

      if (!expandedLeafNodesRef.current.find((n) => n.id === node.id)) {
        addToExpandedLeaves(node)
      }

      expandNode(node).then((isChildrenNodesFetched) => {
        if (options.showCheckBox) {
          // Use functional setState to read latest selectedIds value
          setSelectedIds((prevSelectedIds) => {
            const selectedIdsData = [...prevSelectedIds]
            const isParentSelected = selectedIdsData.includes(node.id)

            if (node.children) {
              if (isParentSelected) {
                // If parent is selected, select all children
                node.children.forEach((child) => {
                  if (!selectedIdsData.includes(child.id)) {
                    selectedIdsData.push(child.id)
                  }
                })
              } else {
                // If all children are selected, select parent
                const allChildrenSelected = node.children.every((child) => selectedIdsData.includes(child.id))
                if (allChildrenSelected && node.children.length > 0) {
                  selectedIdsData.push(node.id)
                }
              }
            }

            // Propagate selection state upward to further ancestors
            let ancestor = node.parent
            while (ancestor) {
              if (options.rootId != null && ancestor.id === options.rootId) break
              if (ancestor.children && ancestor.children.length > 0) {
                const allChildrenSelected = ancestor.children.every((child) => selectedIdsData.includes(child.id))
                if (allChildrenSelected) {
                  if (!selectedIdsData.includes(ancestor.id)) {
                    selectedIdsData.push(ancestor.id)
                  }
                } else {
                  const idx = selectedIdsData.indexOf(ancestor.id)
                  if (idx !== -1) {
                    selectedIdsData.splice(idx, 1)
                  }
                }
              }
              ancestor = ancestor.parent
            }

            const updatedIds = Array.from(new Set(selectedIdsData))

            // Notify parent component of selection change
            if (typeof props.onSelect === 'function' && updatedIds.length !== prevSelectedIds.length) {
              props.onSelect(updatedIds, baseNodesRef.current)
            }

            return updatedIds
          })
        }

        setTimeout(() => {
          setIsExpandActionInProgress(false)
        }, 0)
        updateNodes()
      })
    },
    [
      cancelLoading,
      removeFromLeaves,
      addToExpandedLeaves,
      expandNode,
      options.showCheckBox,
      options.rootId,
      updateNodes,
      props
    ]
  )

  // Action: Collapse
  const collapseAction = useCallback(
    (node: TreeNode) => {
      cancelLoading()

      node.isExpanded = false
      expandedLeafNodesRef.current
        .filter((fRef) => isDescendant(node.id, fRef) || node.id === fRef.id)
        .forEach((leaf) => {
          removeFromExpandedRecursive(leaf, node)
          removeFromLeaves(leaf)
        })

      // Legacy: set parent as end of branch if no other branches exist
      if (node.parentId != null && !expandedLeafNodesRef.current.find((n) => n.id === node.parentId)) {
        // Find any other expanded node that is a descendant of the same parent (legacy: isDescendant)
        const otherDescendedFolder = expandedNodesRef.current.find((fRef) =>
          isDescendant(node.parentId as string | number, fRef)
        )

        if (!otherDescendedFolder) {
          // Find parent node in expandedNodes
          const parent = expandedNodesRef.current.find((n) => n.id === node.parentId)
          if (parent) {
            addToExpandedLeaves(parent)
          }
        }
      }

      updateNodes()
    },
    [cancelLoading, isDescendant, removeFromExpandedRecursive, removeFromLeaves, addToExpandedLeaves, updateNodes]
  )

  // Helper: Preload virtual nodes
  const preloadVirtualNodes = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const promises: Promise<void>[] = []

      baseNodesRef.current.forEach((node) => {
        if (typeof node.id === 'number' && node.id < 1) {
          promises.push(loadBaseNode(node))
        }
      })

      Promise.all(promises).then(() => {
        // All base nodes loaded, updating tree nodes
        updateNodes(() => resolve())
      })
    })
  }, [loadBaseNode, updateNodes])

  // Helper: Load nodes
  const loadNodes = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const promises: Promise<any>[] = []

      baseNodesRef.current.forEach((node) => {
        promises.push(loadBaseNode(node))
      })

      expandedNodesRef.current
        .filter((node) => !baseNodesRef.current.find((n) => n.id === node.id))
        .forEach((node) => {
          node.minimalInfo = true
          promises.push(expandNode(node))
        })

      Promise.all(promises).then(() => {
        expandedNodesRef.current.forEach((node) => {
          if (node.parent) {
            nodeReplacement(node, node.parent.children || [])
          }
        })

        updateNodes(() => resolve())
      })
    })
  }, [loadBaseNode, expandNode, nodeReplacement, updateNodes])

  // Helper: Load open path
  const loadOpenPath = useCallback(
    (list: (number | string)[]): Promise<void> => {
      const preloadedNodes = baseNodesRef.current.reduce((acc: TreeNode[], baseNode) => {
        return [...acc, ...(baseNode.wasLoaded ? baseNode.children || [] : [baseNode])]
      }, [])

      return new Promise((resolve, reject) => {
        const promises: Promise<any>[] = []

        list.forEach((id) => {
          const preloadedNode = preloadedNodes.find((node) => node.id === id)
          if (preloadedNode) {
            // Preloaded node found, push promise to fetch FSI path
            promises.push(api.files.getFsiPath(Number(id)).then((path: any[]) => ({ path, preloadedNode })))
          }
        })

        Promise.all(promises)
          .then((results) => {
            if (!results || results.length === 0) {
              resolve()
              return
            }

            results.forEach(({ path, preloadedNode }) => {
              if (path && path.length) {
                let currentNode: TreeNode | undefined = preloadedNode

                path
                  .slice()
                  .reverse()
                  .forEach((pathNode: any, index: number) => {
                    const isLast = index === path.length - 1

                    if (currentNode) {
                      if (currentNode.id === pathNode.id) {
                        if (!isLast) {
                          addToExpandedLeaves(currentNode)
                        }
                      } else {
                        const parentNode =
                          baseNodesRef.current.find((n) => n.id === pathNode.id) ||
                          expandedNodesRef.current.find((n) => n.id === pathNode.id)

                        if (parentNode) {
                          if (!isLast) {
                            addToExpandedLeaves(parentNode)
                          }

                          if (parentNode.children) {
                            const childIndex = parentNode.children.findIndex((c) => c.id === currentNode!.id)
                            if (childIndex === -1) {
                              parentNode.children.push(currentNode)
                            }
                          }

                          Object.assign(currentNode, {
                            parent: parentNode,
                            parentId: parentNode.id
                          })

                          currentNode = parentNode
                        }
                      }
                    }
                  })
              }
            })

            expandedNodesRef.current = Array.from(new Set(expandedNodesRef.current.map((n) => n.id)))
              .map((id) => expandedNodesRef.current.find((n) => n.id === id)!)
              .reverse()

            expandedNodesRef.current.forEach((node) => {
              setLevelsAccordingToAncestor(node)
            })

            resolve()
          })
          .catch(reject)
      })
    },
    [api, addToExpandedLeaves, setLevelsAccordingToAncestor]
  )

  // Main: Load data
  const loadData = useCallback(() => {
    baseNodesRef.current = []
    expandedNodesRef.current = []
    expandedLeafNodesRef.current = []

    const userRole = service.auth.getRole()
    options.leafIds = options.leafIds || []

    if (options.showRC) {
      const node = createRcNode()
      if (node) {
        baseNodesRef.current.push(node)
      }
    }

    // Legacy parity: specialist/sponsor/manager get both study and site root
    if (userRole === constant.user.roles.ETMF_SITE_USER) {
      baseNodesRef.current.push(createSiteRootNode())
    } else if (userRole === constant.user.roles.ETMF_STUDY_USER) {
      baseNodesRef.current.push(createStudyRootNode())
    } else if (
      [
        constant.user.roles.ETMF_SPECIALIST,
        constant.user.roles.ETMF_SPONSOR,
        constant.user.roles.ETMF_MANAGER
      ].includes(userRole ?? '')
    ) {
      baseNodesRef.current.push(createStudyRootNode())
      baseNodesRef.current.push(createSiteRootNode())
    }

    // EOS node logic: add root node and expand
    if (options.eosMediaTransferMode && options.rootId) {
      const rootFolderNode: TreeNode = {
        level: 1,
        hasChildren: true,
        type: 'FOLDER',
        children: [],
        id: options.rootId,
        isExpanded: true,
        parentId: null,
        name: ''
      }

      getFile(options.rootId).then((data: any) => {
        if (data) {
          // Match legacy: pick id, name, type, systemFolderType, parentId, templateId from API response
          Object.assign(rootFolderNode, {
            id: data.id ?? rootFolderNode.id,
            name: data.name || data.fileName || rootFolderNode.name,
            type: data.type ?? rootFolderNode.type,
            systemFolderType: data.systemFolderType,
            parentId: data.parentId ?? rootFolderNode.parentId,
            templateId: data.templateId
          })
        }
        // Add node and expand after name is set
        baseNodesRef.current.push(rootFolderNode)
        expandAction(rootFolderNode)
      })

      setAddNewFolderModalClasses({
        modalClass: 'level-two-modal',
        backDropClass: 'level-two-backdrop'
      })
    }

    if (options.showSystem) {
      baseNodesRef.current.push(createSystemRootNodes())
    }

    if (options.rootId) {
      options.leafIds.push(options.rootId)
    }

    updateNodes()

    // Auto-load children for any base node that is expanded (e.g., My Sites root)
    baseNodesRef.current.forEach((node) => {
      if (node.isExpanded) {
        loadChildNodes(node)
      }
    })

    preloadVirtualNodes().then(() => {
      loadOpenPath([...options.leafIds!, ...options.openIds!]).then(() => {
        loadNodes()
      })
    })
  }, [
    service,
    constant,
    options,
    createRcNode,
    createSiteRootNode,
    createStudyRootNode,
    createSystemRootNodes,
    getFile,
    updateNodes,
    preloadVirtualNodes,
    loadOpenPath,
    loadNodes
  ])

  // Action: Remove selected ID
  const removeSelectedId = useCallback(
    (id: number | string) => {
      const selectedIdsData = [...selectedIds]
      const index = selectedIdsData.indexOf(id)

      if (index !== -1) {
        selectedIdsData.splice(index, 1)
        setSelectedIds(selectedIdsData)

        if (typeof props.onSelect === 'function') {
          props.onSelect(Array.from(new Set(selectedIdsData)), baseNodesRef.current)
        }
      }
    },
    [selectedIds, props]
  )

  // Action: Add selected ID
  const addSelectedId = useCallback(
    (id: number | string) => {
      const selectedIdsData = [...selectedIds, id]
      setSelectedIds(selectedIdsData)

      if (typeof props.onSelect === 'function') {
        props.onSelect(Array.from(new Set(selectedIdsData)), baseNodesRef.current)
      }
    },
    [selectedIds, props]
  )

  // Action: Start expand action progress
  const startExpandActionProgress = useCallback(() => {
    setIsExpandActionInProgress(true)
  }, [])

  // Action: Update partial selected IDs
  const updatePartialSelectedIds = useCallback(
    (data: number | string, isRemoveAction?: boolean) => {
      const partialSelectionsData = [...partialSelectedIds]
      const index = partialSelectionsData.indexOf(data)

      if (index === -1 && !isRemoveAction) {
        setPartialSelectedIds([...partialSelectionsData, data])
      }
      if (index !== -1 && isRemoveAction) {
        partialSelectionsData.splice(index, 1)
        setPartialSelectedIds([...partialSelectionsData])
      }
    },
    [partialSelectedIds]
  )

  // Render
  return (
    <Loader blocking={isLoading}>
      <div className="filepicker-wrapper" style={{ minHeight: '300px' }}>
        <div className="filepicker">
          <div className="movecopy-wrapper">
            <ul className="tree-container">
              {baseNodes.map((node) => (
                <FsiPickerNode
                  key={node.id}
                  node={node}
                  selector={options.selector}
                  selectedId={selectedIds.length > 0 ? selectedIds[0] : null}
                  selectedIds={selectedIds}
                  onExpand={expandAction}
                  onCollapse={collapseAction}
                  onSelect={selectAction}
                  isNodeSelectable={props.isNodeSelectable}
                  isNodeExpandable={props.isNodeExpandable}
                  maxDepth={options.maxDepth}
                  showRadio={options.showRadio}
                  showCheckBox={options.showCheckBox}
                  stateSelection={props.stateSelection}
                  isSetupUsersAndWorkflowsFlow={props.isSetupUsersAndWorkflowsFlow}
                  isUserSponsorInstructionCoordinator={props.isUserSponsorInstructionCoordinator}
                  removeSelectedId={removeSelectedId}
                  addSelectedId={addSelectedId}
                  isExpandActionInProgress={isExpandActionInProgress}
                  startExpandActionProgress={startExpandActionProgress}
                  partialSelectedIds={partialSelectedIds}
                  updatePartialSelectedIds={updatePartialSelectedIds}
                  rootId={options.rootId ?? undefined}
                  eosMediaDownloadMediFlow={options.eosMediaTransferMode}
                />
              ))}
            </ul>
          </div>
        </div>
      </div>
      {props.showAddNewFolderModal && (
        <div className="add-new-folder-container">
          <AddNewFolderModalWrapper
            parentFolderId={options.rootId ?? undefined}
            modalLevelClasses={addNewFolderModalClasses?.modalClass}
          >
            {translate('ADMIN_ACTION_ADD_NEW_FOLDER')}
          </AddNewFolderModalWrapper>
        </div>
      )}
    </Loader>
  )
}

// Re-export TreeNode for convenience
export type { TreeNode }

export default FsiPicker
